import React, { useContext, useEffect, useState } from 'react';
import { Button } from '../../../components/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import { useNavigate } from 'react-router-dom';
import { useAppTrackLocations } from '../../../hooks/useAppTrackLocation';
import { AppTrackLocation } from '../../../models/AppTrackLocation';
import Loading from '../../../components/Loading';
import { Heading } from '../../../components/Heading';
import { PlusIcon, EllipsisVerticalIcon, EyeIcon, PencilIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/Dropdown';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '../../../components/Dialog';
import Pagination from '../../../components/Pagination';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { Select } from '../../../components/Select';
import { Text } from '../../../components/Text';
import { useWarehouses } from '../../../hooks/useWarehouse';
import { UserContext } from '../../../context/userContext';

const COLUMNS = [
    { key: 'name', label: 'Location Name' },
    { key: 'lat', label: 'Latitude' },
    { key: 'lng', label: 'Longitude' },
];
const SORT_OPTIONS = [
    { label: 'Name (A-Z)', value: 'az' },
    { label: 'Name (Z-A)', value: 'za' },
];

const AppTrackLocationPage: React.FC = () => {
    const { currentUser } = useContext(UserContext);
    const { loading, error, fetchTrackLocationsPaginated, deleteTrackLocation } = useAppTrackLocations();
    const { warehouses } = useWarehouses();
    const [trackLocations, setTrackLocations] = useState<AppTrackLocation[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const perPage = 10;
    const navigate = useNavigate();
    const [sortOrder, setSortOrder] = useState('az');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
    const [isOpen, setIsOpen] = useState(false); // Dialog open state
    const [isDeleting, setIsDeleting] = useState(false); // Loading state for deletion
    const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterWarehouseId, setFilterWarehouseId] = useState('');

    useEffect(() => {
        document.title = "Track Locations";
    }, []);

    useEffect(() => {
        (async () => {
            const res = await fetchTrackLocationsPaginated(currentPage, perPage, searchQuery || undefined, filterWarehouseId || undefined);
            if (res) {
                setTrackLocations(res.locations);
                setTotal(res.total);
            }
        })();
    }, [currentPage, searchQuery, filterWarehouseId, fetchTrackLocationsPaginated]);

    const resetFilters = () => {
        setFilterWarehouseId('');
    };

    const activeFilterCount = [filterWarehouseId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterWarehouseId ? [{
            key: 'warehouse',
            label: `Warehouse: ${warehouses?.find((w) => w.id?.toString() === filterWarehouseId)?.warehouse_name || filterWarehouseId}`,
            onClear: () => setFilterWarehouseId(''),
        }] : []),
    ];

    const handleOpenDeleteDialog = (locationId: string) => {
        setSelectedLocationId(locationId);
        setIsOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedLocationId) return;

        setIsDeleting(true);
        try {
            await deleteTrackLocation(Number(selectedLocationId));
            const res = await fetchTrackLocationsPaginated(currentPage, perPage, searchQuery || undefined, filterWarehouseId || undefined);
            if (res) {
                setTrackLocations(res.locations);
                setTotal(res.total);
            }
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to delete location:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Locations</Heading>
                </div>
                <div className="flex items-center gap-2">
                    <Button onClick={() => navigate('/gp/app/track/locations/add')}><PlusIcon />Create new location</Button>
                </div>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search location…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />
            <ul>
                {loading && <div className="w-full text-center h-full py-36 justify-center"><Loading /></div>}
                {error && <p className="py-16 text-center text-red-700">{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    {isColumnVisible(columnVisibility, 'name') && <TableHeader>Location Name</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'lat') && <TableHeader>Latitude</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'lng') && <TableHeader>Longitude</TableHeader>}
                                    <TableHeader className='text-center'>Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {trackLocations.length > 0 ? [...trackLocations].sort((a, b) => {
                                    const cmp = (a.location_name || '').localeCompare(b.location_name || '');
                                    return sortOrder === 'az' ? cmp : -cmp;
                                }).map((location, index) => (
                                    <TableRow key={location.id}>
                                        <TableCell>{(currentPage - 1) * perPage + index + 1}</TableCell>
                                        {isColumnVisible(columnVisibility, 'name') && <TableCell>{location.location_name}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'lat') && <TableCell>{location.latitude}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'lng') && <TableCell>{location.longitude}</TableCell>}
                                        <TableCell className="py-5 flex gap-4 text-zinc-500 justify-center">
                                            <a onClick={() => navigate(`/gp/app/track/locations/edit/${location.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <PencilIcon className='h-5 w-5' />
                                            </a>
                                            <a onClick={() => navigate(`/gp/app/track/locations/view/${location.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <EyeIcon className='h-5 w-5' />
                                            </a>
                                            <Dropdown>
                                                <DropdownButton plain aria-label="More options" className='h-5 w-5' >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom end">
                                                    <DropdownItem onClick={() => handleOpenDeleteDialog(location.id.toString())}>Delete</DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8">No location found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(total / perPage)}
                            onPageChange={setCurrentPage}
                            totalResult={total}
                            dataName={'Locations'}
                        />
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                {(currentUser?.role?.toString() === "1") && (
                    <div>
                        <Text>By Warehouse</Text>
                        <Select value={filterWarehouseId} onChange={(e) => setFilterWarehouseId(e.target.value)}>
                            <option value="">All Warehouses</option>
                            {warehouses?.map((warehouse) => (
                                <option key={warehouse.id} value={warehouse.id?.toString()}>
                                    {warehouse.warehouse_name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete Location</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this tracking location? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AppTrackLocationPage;
